/**
 * DbSearchHistory - 搜索历史组件
 *
 * 特性：
 * - 集成 DbSearchBar 搜索栏
 * - 支持搜索历史记录展示、单个删除、全部清空
 * - 支持热门搜索/搜索发现（含刷新和查看更多）
 * - 支持返回按钮
 * - 支持自定义标题文案
 * - 标签样式可自定义
 */

import React, { useMemo, useCallback, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import type {
    DbSearchHistoryProps,
    SearchHistoryItem,
    HotSearchItem,
} from './DbSearchHistory.types';
import { createStyles } from './DbSearchHistory.styles';
import DbSearchBar from '../DbSearchBar';
import DbIcon from '../DbIcon';

export const DbSearchHistory: React.FC<DbSearchHistoryProps> = ({
    // 搜索栏
    searchBarProps,
    // 搜索历史
    historyList = [],
    maxHistory = 20,
    historyTitle = '搜索发现',
    onHistoryPress,
    onHistoryDelete,
    onHistoryClear,
    showDeleteIcon = false,
    showClearButton = true,
    // 热门搜索
    hotList = [],
    hotTitle = '搜索发现',
    onHotPress,
    showRefreshButton = false,
    onRefresh,
    showMoreButton = false,
    onMore,
    // 返回按钮
    showBack = true,
    onBack,
    // 搜索
    onSearch,
    // 样式
    style,
    searchBarContainerStyle,
    titleStyle,
    tagStyle,
    tagTextStyle,
    historyContainerStyle,
    hotContainerStyle,
}) => {
    const styles = useMemo(() => createStyles(), []);
    const [searchText, setSearchText] = useState('');

    // 处理搜索
    const handleSearch = useCallback(
        (text: string) => {
            onSearch?.(text);
        },
        [onSearch]
    );

    // 处理搜索文本变化
    const handleChangeText = useCallback(
        (text: string) => {
            setSearchText(text);
            searchBarProps?.onChangeText?.(text);
        },
        [searchBarProps]
    );

    // 处理搜索提交
    const handleSubmit = useCallback(
        (text: string) => {
            handleSearch(text);
            searchBarProps?.onSubmit?.(text);
        },
        [handleSearch, searchBarProps]
    );

    // 处理历史项点击
    const handleHistoryPress = useCallback(
        (item: SearchHistoryItem) => {
            onHistoryPress?.(item);
            handleSearch(item.text);
        },
        [onHistoryPress, handleSearch]
    );

    // 处理热门搜索点击
    const handleHotPress = useCallback(
        (item: HotSearchItem) => {
            onHotPress?.(item);
            handleSearch(item.text);
        },
        [onHotPress, handleSearch]
    );

    // 限制历史记录数量
    const displayHistory = useMemo(
        () => historyList.slice(0, maxHistory),
        [historyList, maxHistory]
    );

    // 渲染搜索栏
    const renderSearchBar = () => (
        <View style={[styles.searchBarContainer, searchBarContainerStyle]}>
            {showBack && (
                <TouchableOpacity
                    style={styles.backButton}
                    onPress={onBack}
                    hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
                >
                    <DbIcon
                        name="chevron-back"
                        family="Ionicons"
                        size={24}
                        color="#333333"
                    />
                </TouchableOpacity>
            )}
            <View style={styles.searchBarWrapper}>
                <DbSearchBar
                    placeholder="input search text"
                    showSearchButton
                    searchButtonText="搜索"
                    showCamera
                    {...searchBarProps}
                    value={searchBarProps?.value ?? searchText}
                    onChangeText={handleChangeText}
                    onSubmit={handleSubmit}
                />
            </View>
        </View>
    );

    // 渲染历史记录标签
    const renderHistoryTag = (item: SearchHistoryItem) => {
        if (showDeleteIcon) {
            return (
                <View key={item.id} style={[styles.tagWithDelete, tagStyle]}>
                    <TouchableOpacity onPress={() => handleHistoryPress(item)}>
                        <Text style={[styles.tagText, tagTextStyle]}>{item.text}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={styles.deleteIcon}
                        onPress={() => onHistoryDelete?.(item)}
                        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                    >
                        <DbIcon
                            name="close-circle"
                            family="Ionicons"
                            size={14}
                            color="#CCCCCC"
                        />
                    </TouchableOpacity>
                </View>
            );
        }

        return (
            <TouchableOpacity
                key={item.id}
                style={[styles.tag, tagStyle]}
                onPress={() => handleHistoryPress(item)}
                activeOpacity={0.7}
            >
                <Text style={[styles.tagText, tagTextStyle]}>{item.text}</Text>
            </TouchableOpacity>
        );
    };

    // 渲染搜索历史区域
    const renderHistory = () => {
        if (displayHistory.length === 0) return null;

        return (
            <View style={[historyContainerStyle]}>
                {/* 标题行 */}
                <View style={styles.sectionHeader}>
                    <Text style={[styles.sectionTitle, titleStyle]}>{historyTitle}</Text>
                    <View style={styles.sectionActions}>
                        {showClearButton && (
                            <TouchableOpacity
                                style={styles.actionButton}
                                onPress={onHistoryClear}
                                hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                            >
                                <DbIcon
                                    name="trash-outline"
                                    family="Ionicons"
                                    size={18}
                                    color="#999999"
                                />
                            </TouchableOpacity>
                        )}
                    </View>
                </View>

                {/* 标签列表 */}
                <View style={styles.tagsContainer}>
                    {displayHistory.map(renderHistoryTag)}
                </View>
            </View>
        );
    };

    // 渲染热门搜索标签
    const renderHotTag = (item: HotSearchItem) => (
        <TouchableOpacity
            key={item.id}
            style={[styles.tag, tagStyle]}
            onPress={() => handleHotPress(item)}
            activeOpacity={0.7}
        >
            <Text style={[styles.tagText, tagTextStyle]}>{item.text}</Text>
        </TouchableOpacity>
    );

    // 渲染热门搜索区域
    const renderHotSearch = () => {
        if (hotList.length === 0) return null;

        return (
            <View style={[hotContainerStyle]}>
                {/* 标题行 */}
                <View style={styles.sectionHeader}>
                    <Text style={[styles.sectionTitle, titleStyle]}>{hotTitle}</Text>
                    <View style={styles.sectionActions}>
                        {showRefreshButton && (
                            <TouchableOpacity
                                style={styles.actionButton}
                                onPress={onRefresh}
                                hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                            >
                                <DbIcon
                                    name="refresh-outline"
                                    family="Ionicons"
                                    size={18}
                                    color="#999999"
                                />
                            </TouchableOpacity>
                        )}
                        {showMoreButton && (
                            <TouchableOpacity
                                style={styles.actionButton}
                                onPress={onMore}
                                hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                            >
                                <DbIcon
                                    name="eye-outline"
                                    family="Ionicons"
                                    size={18}
                                    color="#999999"
                                />
                            </TouchableOpacity>
                        )}
                    </View>
                </View>

                {/* 标签列表 */}
                <View style={styles.tagsContainer}>
                    {hotList.map(renderHotTag)}
                </View>
            </View>
        );
    };

    return (
        <View style={[styles.container, style]}>
            {/* 搜索栏 */}
            {renderSearchBar()}

            {/* 内容区域 */}
            <ScrollView
                style={styles.contentContainer}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
            >
                {/* 热门搜索/搜索发现 */}
                {renderHotSearch()}

                {/* 搜索历史 */}
                {renderHistory()}
            </ScrollView>
        </View>
    );
};

DbSearchHistory.displayName = 'DbSearchHistory';

export default DbSearchHistory;
export type {
    DbSearchHistoryProps,
    SearchHistoryItem,
    HotSearchItem,
} from './DbSearchHistory.types';
